import axios from 'axios';

// Determine the API base URL based on environment
const getBaseURL = () => {
  // Check if we're in development (localhost)
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:8000';
  }
  // Production - use the same origin with HTTPS (nginx will proxy to backend)
  return `${window.location.protocol}//${window.location.host}`;
};


const api = axios.create({
  baseURL: getBaseURL(),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Use the same CSRF token function as AuthContext
const getCSRFToken = () => {
  const name = 'gopak_csrftoken';
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
};

// Add CSRF token to requests
api.interceptors.request.use((config) => {
  const token = getCSRFToken();
  if (token) {
    config.headers['X-CSRFToken'] = token;
  }
  console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
  return config;
});

// Connection error callback - will be set by ConnectionProvider
let onConnectionError = null;

export const setConnectionErrorHandler = (handler) => {
  onConnectionError = handler;
};

api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    // Connection is working, clear any error state
    if (onConnectionError) {
      onConnectionError(false);
    }
    return response;
  },
  (error) => {
    console.error(`API Error: ${error.response?.status || 'Network Error'} ${error.config?.url}`);
    console.error('Error details:', error.response?.data);
    
    // Check if this is a network/connection error (no response from server)
    if (!error.response && error.message === 'Network Error') {
      console.error('Connection lost - server may be updating');
      if (onConnectionError) {
        onConnectionError(true);
      }
    }
    
    // Also check for common "server down" status codes
    if (error.response?.status === 502 || error.response?.status === 503 || error.response?.status === 504) {
      console.error('Server unavailable - may be updating');
      if (onConnectionError) {
        onConnectionError(true);
      }
    }

    // Handle session expiry - silently try to re-establish rather than breaking
    if (error.response?.status === 401 || error.response?.status === 403) {
      console.warn('Session may have expired - auth check needed on next interaction');
    }

    return Promise.reject(error);
  }
);

// Manufacturing API functions only
export const manufacturingAPI = {
  // General import validation endpoint
  validateImport: (formData) => api.post('/api/validate-import/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  searchItemsForBOM: (params = {}) => api.get('/api/bom/search_items_for_bom/', { params }),

  // Items
  items: {
    list: (params = {}) => api.get('/api/items/', { params }),
    listAll: () => api.get('/api/items/', { params: { page_size: 1000 } }),
    withChildren: (params = {}) => api.get('/api/bom/with_children/', { params }),
    create: (data) => api.post('/api/items/', data),
    get: (id) => api.get(`/api/items/${id}/`),
    update: (id, data) => api.patch(`/api/items/${id}/`, data),
    delete: (id) => api.delete(`/api/items/${id}/`),
    importItems: (formData) => api.post('/api/items/import/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    importTableTopBoms: (formData) => api.post('/api/items/import_table_top_boms/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    getCount: () => api.get('/api/items/count/'), 
    importWeights: (formData) => api.post('/api/items/import_weights/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    importCostPrices: (formData) => api.post('/api/items/import_cost_prices/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    importSageProducts: (formData) => api.post('/api/items/import_sage_products/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    updateWeight: (id, weight) => api.patch(`/api/items/${id}/update_weight/`, { weight }),
    validateDescriptionImport: (formData) => api.post('/api/items/validate_description_import/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    importDescriptions: (formData) => api.post('/api/items/import_descriptions/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    importAdditionalInstructions: (formData) => api.post('/api/items/import_additional_instructions/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    parentItems: (params = {}) => api.get('/api/bom/parent_items/', { params }),
    exportByPrefix: (params = {}) => api.get('/api/items/export/', {
      params,
      responseType: 'blob',
    }),

  },

  reconciliation: {
    getReport: () => api.get('/api/manufacturing/reconciliation/report/'),
    createWorkOrders: (data) => api.post('/api/manufacturing/reconciliation/create-work-orders/', data),
    // NEW: Full validation endpoint
    fullValidation: () => axios.get('/api/reconciliation/full-validation/'),
    
    // NEW: Create missing work orders
    createMissingWorkOrders: (data) => axios.post('/api/reconciliation/create-missing/', data),
    batchBomExplosion: (data) => axios.post('/api/reconciliation/batch-bom-explosion/', data),
  },
  

  itemRouting: {
    list: (params = {}) => api.get('/api/item-routing/', { params }),
    create: (data) => api.post('/api/item-routing/', data),
    update: (id, data) => api.patch(`/api/item-routing/${id}/`, data),
    delete: (id) => api.delete(`/api/item-routing/${id}/`),
  },

  // Operations
  operations: {
    list: () => api.get('/api/operations/'),
    create: (data) => api.post('/api/operations/', data),
    get: (id) => api.get(`/api/operations/${id}/`),
    update: (id, data) => api.patch(`/api/operations/${id}/`, data),
    delete: (id) => api.delete(`/api/operations/${id}/`),
    importOperations: (formData) => api.post('/api/operations/import/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  },

  // Employees
  employees: {
    list: () => api.get('/api/employees/'),
    create: (data) => api.post('/api/employees/', data),
    get: (id) => api.get(`/api/employees/${id}/`),
    update: (id, data) => api.patch(`/api/employees/${id}/`, data),
    delete: (id) => api.delete(`/api/employees/${id}/`),
  },

  // BOM
  bom: {
    list: (params = {}) => api.get('/api/bom/', { params }),
    withChildren: (params = {}) => api.get('/api/bom/with_children/', { params }),
    parentItems: (params = {}) => api.get('/api/bom/parent_items/', { params }),
    create: (data) => api.post('/api/bom/', data),
    get: (id) => api.get(`/api/bom/${id}/`),
    update: (id, data) => api.patch(`/api/bom/${id}/`, data),
    delete: (id) => api.delete(`/api/bom/${id}/`),
    explodeRequirements: (data) => api.post('/api/bom/explode_requirements/', data),
    duplicateBOM: (data) => api.post('/api/bom/duplicate_bom/', data),
    importBOM: (formData) => api.post('/api/bom/import_bom/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    validateImport: (formData) => api.post('/api/bom/validate_import/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    importHierarchicalBOM: (formData) => api.post('/api/bom/import_hierarchical_bom/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    validateZeroOutAdhesive: (formData) => api.post('/api/bom/validate_zero_out_adhesive/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    zeroOutAdhesive: (formData) => api.post('/api/bom/zero_out_adhesive/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    updateBOMQuantities: (formData) => api.post('/api/bom/update_bom_quantities/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    validateComponentSwap: (formData) => api.post('/api/bom/validate-component-swap/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    componentSwap: (formData) => api.post('/api/bom/component-swap/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    validatePolyedgeReplacement: (formData) => api.post('/api/bom/validate_polyedge_replacement/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    polyedgeReplacement: (formData) => api.post('/api/bom/polyedge_replacement/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    
  },

  // Stock Levels 
  stockLevels: {
    list: (params = {}) => api.get('/api/stock-levels/', { params }),
    get: (id) => api.get(`/api/stock-levels/${id}/`),
    create: (data) => api.post('/api/stock-levels/', data),
    update: (id, data) => api.patch(`/api/stock-levels/${id}/`, data),
    delete: (id) => api.delete(`/api/stock-levels/${id}/`),
    lowStock: () => api.get('/api/stock-levels/low_stock/'),
    dashboardStats: () => api.get('/api/stock-levels/dashboard_stats/'),  
    inventoryStats: () => api.get('/api/stock-levels/inventory_stats/'),
    withOrderRequirements: (params = {}) => api.get('/api/stock-levels/with_order_requirements/', { params }),
    bomItemExplosion: (params = {}) => api.get('/api/stock-levels/bom_item_explosion/', { params }),
    bomItemDetail: (params = {}) => api.get('/api/stock-levels/bom_item_detail/', { params }),
    reduceSurplus: (data) => api.post('/api/stock-levels/reduce_surplus/', data),
    importStock: (formData) => api.post('/api/stock-levels/import_stock/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    setTempStock10000: (formData) => api.post('/api/stock-levels/set_temp_stock_10000/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  },

  // Work Order Executions
  workOrderExecutions: {
    list: (params = {}) => api.get('/api/work-order-executions/', { params }),
    create: (data) => api.post('/api/work-order-executions/', data),
    get: (id) => api.get(`/api/work-order-executions/${id}/`),
    update: (id, data) => api.patch(`/api/work-order-executions/${id}/`, data),
    delete: (id) => api.delete(`/api/work-order-executions/${id}/`),
  },
  
  // Stock Transactions
  stockTransactions: {
    list: (params = {}) => api.get('/api/stock-transactions/', { params }),
    get: (id) => api.get(`/api/stock-transactions/${id}/`),
    create: (data) => api.post('/api/stock-transactions/', data),
  },

  // Work Orders 
  workOrders: {
    list: (params = {}) => api.get('/api/work-orders/', { params }),
    get: (id) => api.get(`/api/work-orders/${id}/`),
    create: (data) => api.post('/api/work-orders/', data),
    update: (id, data) => api.patch(`/api/work-orders/${id}/`, data),
    fullUpdate: (id, data) => api.put(`/api/work-orders/${id}/`, data),
    delete: (id) => api.delete(`/api/work-orders/${id}/`),
    getMaterialRequirements: (workOrderId) => 
        api.get(`/api/work-orders/${workOrderId}/material_requirements/`),
    createFromBomExplosion: (data) => api.post('/api/work-orders/create_from_bom_explosion/', data),
    releaseToProduction: (id) => api.post(`/api/work-orders/${id}/release_to_production/`),
    bulkImport: (data) => api.post('/api/work-orders/bulk_import/', data),
    checkMaterialAvailability: (workOrderId, quantity) => 
        api.get(`/api/work-orders/${workOrderId}/check_material_availability/?quantity=${quantity}`),
    consumeMaterials: (workOrderId, quantity) =>
        api.post(`/api/work-orders/${workOrderId}/consume_materials/`, { quantity }),
    ordersNeedingItem: (itemId) =>
        api.get(`/api/work-orders/orders_needing_item/`, { params: { item_id: itemId } }),
    importWorkOrders: (formData) => api.post('/api/work-orders/import/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  },

  customerOrders: {
    list: (params = {}) => api.get('/api/customer-orders/', { params }),
    get: (id) => api.get(`/api/customer-orders/${id}/`),
    create: (data) => api.post('/api/customer-orders/', data),
    update: (id, data) => api.patch(`/api/customer-orders/${id}/`, data),
    delete: (id) => api.delete(`/api/customer-orders/${id}/`),
    createWorkOrders: (id) => api.post(`/api/customer-orders/${id}/create_work_orders/`),
    shipQuantity: (id, data) => api.post(`/api/customer-orders/${id}/ship_quantity/`, data),
    unshipQuantity: (id, data) => api.post(`/api/customer-orders/${id}/unship_quantity/`, data),
    unshipOrder: (id) => api.post(`/api/customer-orders/${id}/unship_order/`),
    dashboardStats: () => api.get('/api/customer-orders/dashboard_stats/'),
    importOrders: (formData) => api.post('/api/customer-orders/import/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    importSageOrders: (formData) => api.post('/api/customer-orders/import_sage_orders/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    updateDeliveryInfo: (formData) => api.post('/api/import/update-delivery-info/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    allocateAvailableStock: () => api.post('/api/customer-orders/allocate_available_stock/'),
    updateDispatchNotes: (id, notes) => api.patch(`/api/customer-orders/${id}/update_dispatch_notes/`, { dispatch_notes: notes }),
    updateDeliveryService: (id, deliveryService) => api.patch(`/api/customer-orders/${id}/update_delivery_service/`, { delivery_service: deliveryService }),
    updateStatus: (id, newStatus) => api.patch(`/api/customer-orders/${id}/update_status/`, { status: newStatus }),
    shippedDates: () => api.get('/api/customer-orders/shipped_dates/'),

    previewDelete: (id) => api.get(`/api/customer-orders/${id}/preview-delete/`),
    deleteWithCleanup: (id) => api.post(`/api/customer-orders/${id}/delete-with-cleanup/`),
  },


  customers: {
    list: (params) => api.get('/api/customers/', { params }),
    get: (id) => api.get(`/api/customers/${id}/`),
    create: (data) => api.post('/api/customers/', data),
    update: (id, data) => api.put(`/api/customers/${id}/`, data),
    delete: (id) => api.delete(`/api/customers/${id}/`),
    orders: (id) => api.get(`/api/customers/${id}/orders/`),
  },


  orderCodeTypes: {
    list: (params) => api.get('/api/order-code-types/', { params }),
    get: (id) => api.get(`/api/order-code-types/${id}/`),
    create: (data) => api.post('/api/order-code-types/', data),
    update: (id, data) => api.put(`/api/order-code-types/${id}/`, data),
    delete: (id) => api.delete(`/api/order-code-types/${id}/`),
  },

  productCodeMappings: {
    list: (params) => api.get('/api/product-code-mappings/', { params }),
    get: (id) => api.get(`/api/product-code-mappings/${id}/`),
    create: (data) => api.post('/api/product-code-mappings/', data),
    update: (id, data) => api.patch(`/api/product-code-mappings/${id}/`, data),
    delete: (id) => api.delete(`/api/product-code-mappings/${id}/`),
    bulkCreate: (mappings) => api.post('/api/product-code-mappings/bulk_create/', { mappings }),
    bulkDelete: (ids) => api.delete('/api/product-code-mappings/bulk_delete/', { data: { ids } }),
    validateImport: (formData) => api.post('/api/product-code-mappings/validate_import/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    importMappings: (formData) => api.post('/api/product-code-mappings/import_mappings/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  },

  heavyGoods: {
    getSettings: () => api.get('/api/settings/heavy-goods/'),
    updateSettings: (formData) => api.post('/api/settings/heavy-goods/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    getLabel: () => api.get('/api/settings/heavy-goods/label/'),
    deleteLabel: () => api.delete('/api/settings/heavy-goods/label/'),
  },

  dxShipping: {
    getSessionKey: (credentials) => api.post('/api/dx-shipping/get-session-key/', credentials),
    addConsignment: (data, config) => api.post('/api/dx-shipping/add-consignment/', data, config),
    getLabels: (data, config) => api.post('/api/dx-shipping/get-labels/', data, config),
    getConsignmentTracking: (data, config) => api.post('/api/dx-shipping/get-tracking/', data, config),
    deleteConsignment: (data, config) => api.post('/api/dx-shipping/delete-consignment/', data, config),
    releaseConsignment: (data, config) => api.post('/api/dx-shipping/release-consignment/', data, config),
    saveConsignment: (data) => api.post('/api/dx-shipping/save-consignment/', data),
    getConsignments: (orderId) => api.get(`/api/dx-shipping/consignments/${orderId}/`),
    getConsignmentDetail: (consignmentId) => api.get(`/api/dx-shipping/consignments/${consignmentId}/detail/`),
    updateConsignmentStatus: (consignmentId, data) => api.put(`/api/dx-shipping/consignments/${consignmentId}/status/`, data),
    deleteConsignmentRecord: (consignmentId) => api.delete(`/api/dx-shipping/consignments/${consignmentId}/delete/`),
    completeShipment: (consignmentId) => api.post(`/api/dx-shipping/consignments/${consignmentId}/complete/`),
  },

  stockConversion: {
    convert: (data) => api.post('/api/stock-conversion/', data),
  },

  reports: {
    list: () => api.get('/api/reports/'),
    getAlwaysIncludeCodes: (report) => api.get('/api/reports/always-include-codes/', { params: { report } }),
    saveAlwaysIncludeCodes: (report, codes) => api.put('/api/reports/always-include-codes/', { report, codes }),
    itemSearch: (params) => api.get('/api/reports/item-search/', { params }),
    listCodeSheets: (report) => api.get('/api/reports/code-sheets/', { params: { report } }),
    uploadCodeSheet: (formData) => api.post('/api/reports/code-sheets/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    updateCodeSheet: (id, data) => api.patch(`/api/reports/code-sheets/${id}/`, data),
    deleteCodeSheet: (id) => api.delete(`/api/reports/code-sheets/${id}/`),
    bulkCodeSheets: (data) => api.post('/api/reports/code-sheets/bulk/', data),
    downloadCodeSheetTemplate: () => api.get('/api/reports/code-sheets/template/', { responseType: 'blob' }),
    stockPreview: (params = {}) => api.get('/api/reports/stock/preview/', { params }),
    downloadStock: (options = {}) => api.post('/api/reports/stock/', options, {
      responseType: 'blob'
    }),
    stockAdjustmentPreview: (formData) => api.post('/api/reports/stock/adjustment/preview/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    downloadStockAdjustment: (formData) => api.post('/api/reports/stock/adjustment/', formData, {
      responseType: 'blob',
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    inventoryChangePreview: (data) => api.post('/api/reports/inventory-change/preview/', data),
    downloadInventoryChange: (data) => api.post('/api/reports/inventory-change/', data, {
      responseType: 'blob'
    }),
    downloadDispatchExport: (data) => api.post('/api/reports/dispatch-export/', data, {
      responseType: 'blob'
    }),
    stockMovementsPreview: (data) => api.post('/api/reports/stock-movements/preview/', data),
    downloadStockMovements: (data) => api.post('/api/reports/stock-movements/', data, {
      responseType: 'blob'
    }),
    downloadAssemblies: (params = {}) => api.get('/api/reports/assemblies/', {
      params,
      responseType: 'blob',
    }),
    inventoryValuePreview: (params) => api.get('/api/reports/inventory-value/preview/', { params }),
    downloadInventoryValue: (params) => api.get('/api/reports/inventory-value/', {
      params,
      responseType: 'blob',
    }),
    stockTakeImportPreview: (formData) => api.post('/api/reports/stock-take/import/preview/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    applyStockTakeImport: (formData) => api.post('/api/reports/stock-take/import/apply/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    exportStockTakeUnknownCodes: (formData) => api.post('/api/reports/stock-take/import/unknown-codes/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
    }),
    createStockTakeMissingItems: (formData) => api.post('/api/reports/stock-take/import/create-missing/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
    stockTakeUndoLastPreview: () => api.post('/api/reports/stock-take/undo-last/', { dry_run: true }),
    stockTakeUndoLast: () => api.post('/api/reports/stock-take/undo-last/', { dry_run: false }),
    stockTakeTransactionsSincePreview: (params) => api.get('/api/reports/stock-take/since/preview/', { params }),
    downloadStockTakeTransactionsSince: (params) => api.get('/api/reports/stock-take/since/', {
      params,
      responseType: 'blob',
    }),
    bomCostSearch: (params) => api.get('/api/reports/bom-cost/search/', { params }),
    bomCostPreview: (params) => api.get('/api/reports/bom-cost/preview/', { params }),
    downloadBomCost: (params) => api.get('/api/reports/bom-cost/', {
      params,
      responseType: 'blob',
    }),
    downloadBomCostAllTopLevel: (params = {}) => api.get('/api/reports/bom-cost/all/', {
      params,
      responseType: 'blob',
    }),
    bomUsagePreview: (params = {}) => api.get('/api/reports/bom-usage/preview/', { params }),
    downloadBomUsage: (params = {}) => api.get('/api/reports/bom-usage/', {
      params,
      responseType: 'blob',
    }),
    rawMaterialUsagePreview: (params = {}) => api.get('/api/reports/raw-material-usage/preview/', { params }),
    downloadRawMaterialUsage: (params = {}) => api.get('/api/reports/raw-material-usage/', {
      params,
      responseType: 'blob',
    }),
    plannedAdherence: () => api.get('/api/reports/planned-adherence/'),
    plannedAdherenceOrders: (params) => api.get('/api/reports/planned-adherence/orders/', { params }),
    downloadPlannedAdherence: (data) => api.post('/api/reports/planned-adherence/export/', data, {
      responseType: 'blob',
    }),
  },

  customReports: {
    datasets: () => api.get('/api/custom-reports/datasets/'),
    list: () => api.get('/api/custom-reports/'),
    create: (data) => api.post('/api/custom-reports/', data),
    update: (id, data) => api.put(`/api/custom-reports/${id}/`, data),
    remove: (id) => api.delete(`/api/custom-reports/${id}/`),
    run: (data) => api.post('/api/custom-reports/run/', data),
    download: (id) => api.get(`/api/custom-reports/${id}/export/`, { responseType: 'blob' }),
  },

  bomSwap: {
    validate: (data) => api.post('/api/bom/validate-swap/', data),
    execute: (data) => api.post('/api/bom/swap-component/', data),
  },

  hardboardBOM: {
    preview: (formData) => api.post('/api/admin/hardboard-bom/preview/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    apply: (formData) => api.post('/api/admin/hardboard-bom/apply/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  },

  
  procurement: {
    rawMaterials: (params) => api.get('/api/procurement/raw-materials/', { params }),
    setLeadTime: (itemId, leadTimeDays) => api.put(`/api/procurement/lead-time/${itemId}/`, { lead_time_days: leadTimeDays }),
  },

  capacityPlanning: {
    getData: (params) => api.get('/api/capacity-planning/data/', { params }),
    getDetails: (params) => api.get('/api/capacity-planning/details/', { params }),
    getConfig: () => api.get('/api/capacity-planning/config/'),
    updateConfig: (data) => api.put('/api/capacity-planning/config/', data),
  },

  stockAllocations: {
    list: (params = {}) => api.get('/api/stock-allocations/', { params }),
    get: (id) => api.get(`/api/stock-allocations/${id}/`),
    create: (data) => api.post('/api/stock-allocations/', data),
    update: (id, data) => api.patch(`/api/stock-allocations/${id}/`, data),
    delete: (id) => api.delete(`/api/stock-allocations/${id}/`),
    transfer: (data) => api.post('/api/stock-allocations/transfer/', data),
    unallocate: (data) => api.post('/api/stock-allocations/unallocate/', data),
    allocateFromStock: (data) => api.post('/api/stock-allocations/allocate_from_stock/', data),
  },


};

// ════════════════════════════════════════════════════════════════════
// Work Orders V2 — Parallel module (additive; v1 untouched)
// All endpoints under /api/v2/...
// ════════════════════════════════════════════════════════════════════
export const v2 = {
  // Server-driven feature flags for the v2 module. Currently exposes
  // { v1_wo_creation_enabled: bool } — when false the v1 "Create Work Orders"
  // path is hidden so V2 explosion is the only creation route. Callers should
  // fail open (treat errors as flag-enabled) so a flag-endpoint outage never
  // blocks work-order creation.
  featureFlags: {
    get: () => api.get('/api/v2/feature-flags/'),
  },
  // Read-only manufacturing dashboards.
  //   manufacturing(): live operational snapshot (active/overdue/completed
  //     counts, per-operation load, recent completions, order rollups).
  //   kpis({ days }): trailing-window KPIs (throughput, on-time %, scrap %,
  //     cycle time, WIP, top items, busiest operations). days ∈ {7,30,90}.
  dashboard: {
    manufacturing: () => api.get('/api/v2/dashboard/manufacturing/'),
    kpis: (params = {}) => api.get('/api/v2/dashboard/kpis/', { params }),
  },
  workOrders: {
    list: (params = {}) => api.get('/api/v2/work-orders/', { params }),
    retrieve: (id) => api.get(`/api/v2/work-orders/${id}/`),
    startStep: (id, stepId) => api.post(`/api/v2/work-orders/${id}/steps/${stepId}/start/`, {}),
    completeStep: (id, stepId, data) =>
      api.post(`/api/v2/work-orders/${id}/steps/${stepId}/complete/`, data),
    scrap: (id, data) => api.post(`/api/v2/work-orders/${id}/scrap/`, data),
    cancel: (id, data = {}) => api.post(`/api/v2/work-orders/${id}/cancel/`, data),
    adjust: (id, data) => api.post(`/api/v2/work-orders/${id}/adjust/`, data),
    materialAvailability: (id, params = {}) =>
      api.get(`/api/v2/work-orders/${id}/material-availability/`, { params }),
    materialRequirements: (id, params = {}) =>
      api.get(`/api/v2/work-orders/${id}/material-requirements/`, { params }),
    bomTree: (id) => api.get(`/api/v2/work-orders/${id}/bom-tree/`),
    // Manual creation: stock WO (no order), optionally linked to a line;
    // explode=false makes a top-level-only ("empty") WO without explosion.
    createManual: (data) => api.post('/api/v2/work-orders/manual/', data),
    // Combined per-WO chronological feed: material movements + status log
    // entries + order status transitions. Each row tagged with "kind".
    history: (id) => api.get(`/api/v2/work-orders/${id}/history/`),
    // ADMIN ONLY. Safe fields only: due_date, notes, priority.
    adminUpdate: (id, payload) => api.patch(`/api/v2/work-orders/${id}/`, payload),
    // ADMIN ONLY. Body { quantity, notes }: revises quantity_to_produce on a
    // top-level/manual WO; children scale proportionally server-side.
    reviseQuantity: (id, payload) =>
      api.post(`/api/v2/work-orders/${id}/revise-quantity/`, payload),
    // ADMIN ONLY. Body { dry_run, reason }: rewinds a completed WO to planned
    // (stock, materials, allocation and order status reversed).
    undoCompletion: (id, payload = {}) =>
      api.post(`/api/v2/work-orders/${id}/undo-completion/`, payload),
    // ADMIN ONLY. Hard-delete. Pass { cascade: 'tree' } to delete the whole
    // tree (reverses v1 writebacks/reservations/order-status). Omit to delete
    // a single leaf WO (400 if it's top-level or has dependents).
    delete: (id, { cascade } = {}) =>
      api.delete(`/api/v2/work-orders/${id}/`, cascade ? { params: { cascade } } : undefined),
  },
  // Global transaction ledger — paginated, newest-first. See
  // TransactionHistoryV2 for the filter set.
  history: {
    list: (params = {}) => api.get('/api/v2/history/', { params }),
  },
  // Persistent audit trail of every explosion attempt — paginated,
  // newest-first. Params: outcome, source, search, date_from, date_to, page.
  // Each row's `outcome` is created | covered | already_exploded | failed
  // ("covered" = the line was already satisfied by stock, so no WO was needed).
  explosionLog: {
    list: (params = {}) => api.get('/api/v2/explosion-log/', { params }),
  },
  // What was scrapped — paginated, newest-first. Params: search, kind
  // (sign_off | manual), date_from, date_to, page. Each row lists the work
  // orders already sent to make more (`replacement_work_orders`).
  scrap: {
    list: (params = {}) => api.get('/api/v2/scrap/', { params }),
    // Same filters as list, every matching row (capped server-side), as CSV.
    exportCsv: (params = {}) =>
      api.get('/api/v2/scrap/export/', { params, responseType: 'blob' }),
    // Send a stock WO to make more of a scrapped item, linked back to the
    // scrap row. Body { quantity?, explode?, notes? }.
    sendWorkOrder: (id, data = {}) =>
      api.post(`/api/v2/scrap/${id}/send-work-order/`, data),
  },
  // What open v2 orders are short of — required vs free stock vs planned
  // production, computed from the ledger. Read-only.
  shortages: {
    get: () => api.get('/api/v2/shortages/'),
    // Raise a sub-assembly WO in each order tree left short of a
    // made-in-house item. { dry_run: true } previews without writing.
    createWorkOrders: (data = {}) =>
      api.post('/api/v2/shortages/create-work-orders/', data),
  },
  explode: {
    customerOrder: (id, data = {}) =>
      api.post(`/api/v2/customer-orders/${id}/explode/`, data),
    orderLine: (id, data = {}) =>
      api.post(`/api/v2/order-lines/${id}/explode/`, data),
    unsendCustomerOrder: (id, data = {}) =>
      api.post(`/api/v2/customer-orders/${id}/unsend/`, data),
    // Bulk: v2 work orders for EVERY new/confirmed order with no work yet
    // (earliest-due-first; skips orders already planned in v1 or v2).
    // { dry_run: true } lists the queue without writing; { order_ids: [...] }
    // scopes to specific orders (the progress dialog sends one at a time).
    allNewOrders: (data = {}) =>
      api.post('/api/v2/customer-orders/explode-new/', data),
  },
  orderLines: {
    progress: (id) => api.get(`/api/v2/order-lines/${id}/progress/`),
    // Create additional work orders for an already-exploded line (e.g. to
    // replace damaged goods). Body { quantity, notes }. 201 returns the new
    // tree_id and created work_orders; 400 if the line was never exploded or
    // qty<=0; 409 if the order is complete/cancelled.
    remake: (id, payload) => api.post(`/api/v2/order-lines/${id}/remake/`, payload),
  },
  customerOrders: {
    // Shared shop-floor notes for an order (autosaved from any WO row).
    factoryNotes: (id) => api.get(`/api/v2/customer-orders/${id}/factory-notes/`),
    saveFactoryNotes: (id, notes) =>
      api.put(`/api/v2/customer-orders/${id}/factory-notes/`, { notes }),
    summary: (id) => api.get(`/api/v2/customer-orders/${id}/summary/`),
    // Chronological order lifecycle feed (ascending). Returns { events: [...] }
    // where each event has { at, kind, title, detail, user, line_id, wo_number }.
    timeline: (id) => api.get(`/api/v2/customer-orders/${id}/timeline/`),
  },
  // v2-aware capacity planning — mirrors the v1 data/details split.
  //   getData:    all v1 keys, PLUS each column carries a `v2` block and there
  //               is a top-level `daily_v2`; in the aggregate view the v2 fields
  //               are {date: value} maps (wo_total, wo_planned, wo_in_progress,
  //               wo_completed, has_tree_lines, produced_qty, scrapped_qty,
  //               progress_pct).
  //   getDetails: all v1 per-line keys, PLUS `line_id` and a per-line `v2`
  //               object {has_tree, tree_id, wo_total, wo_planned,
  //               wo_in_progress, wo_completed, produced_qty, scrapped_qty,
  //               progress_pct}.
  // Cell math (outstanding = ordered − allocated − shipped) is unchanged from
  // v1. Config load/save stays on the v1 endpoints (not v2-specific).
  capacityPlanning: {
    getData: (params = {}) => api.get('/api/v2/capacity-planning/data/', { params }),
    getDetails: (params = {}) => api.get('/api/v2/capacity-planning/details/', { params }),
  },
  materialMovements: {
    list: (params = {}) => api.get('/api/v2/material-movements/', { params }),
  },
  stockLevels: {
    list: (params = {}) => api.get('/api/v2/stock-levels/', { params }),
  },
  shopFloor: {
    list: (params = {}) => api.get('/api/v2/shop-floor/', { params }),
    completed: (params = {}) =>
      api.get('/api/v2/shop-floor/completed/', { params }),
    completedExport: (params = {}) =>
      api.get('/api/v2/shop-floor/completed/export/', { params, responseType: 'blob' }),
  },
  admin: {
    // dry_run defaults to true server-side; pass { dry_run: false } to execute
    migrateFromV1: (data = {}) =>
      api.post('/api/v2/admin/migrate-from-v1/', data),
    reset: () => api.post('/api/v2/admin/reset/', {}),
    // Reverse the most recent v1→v2 migration. Optional body:
    //   { migration_run_id?: string, force_cancel_completed?: bool }
    undoMigration: (data = {}) =>
      api.post('/api/v2/admin/undo-migration/', data),
    // v2 → v1 swap-back: retires active v2 WOs (banked production kept) and
    // re-plans outstanding lines in v1. dry_run defaults to true server-side;
    // pass { dry_run: false } to execute, { order_ids: [...] } to scope.
    // 409 while V1_WO_CREATION_ENABLED is off.
    revertToV1: (data = {}) =>
      api.post('/api/v2/admin/revert-to-v1/', data),
  },
};

export const authAPI = {
  getCsrfToken: () => api.get('/api/auth/csrf/'),
  login: (credentials) => api.post('/api/auth/login/', credentials),
  logout: () => api.post('/api/auth/logout/'),
  getUser: () => api.get('/api/auth/user/'),
  getPreferences: () => api.get('/api/auth/preferences/'),
  updatePreferences: (prefs) => api.patch('/api/auth/preferences/', prefs),
};

export const adminAPI = {
  getStats: () => api.get('/api/admin/stats/'),
  getUsers: (params) => api.get('/api/admin/users/', { params }),
  createUser: (userData) => api.post('/api/admin/users/create/', userData),
  getUser: (userId) => api.get(`/api/admin/users/${userId}/`),
  updateUser: (userId, userData) => api.patch(`/api/admin/users/${userId}/`, userData),
  deleteUser: (userId) => api.delete(`/api/admin/users/${userId}/`),
};

// External Orders API key management (admin page for the /api/v1/ feed)
export const ordersApiKeysAPI = {
  list: () => api.get('/api/admin/orders-api-keys/'),
  create: (data) => api.post('/api/admin/orders-api-keys/', data),
  revoke: (keyId) => api.post(`/api/admin/orders-api-keys/${keyId}/revoke/`),
  delete: (keyId) => api.delete(`/api/admin/orders-api-keys/${keyId}/`),
  downloadGuide: () =>
    api.get('/api/admin/orders-api-keys/guide.pdf', { responseType: 'blob' }),
  usage: () => api.get('/api/admin/orders-api-keys/usage/'),
};



export const shopFloorAPI = {
  getHighlights: () => api.get('/api/shop-floor/highlights/'),
  saveHighlights: (rules) => api.post('/api/shop-floor/highlights/', rules),
  clearHighlights: () => api.delete('/api/shop-floor/highlights/'),
};

export const tvDashboardAPI = {
  getStats: () => api.get('/api/tv-dashboard/'),
};



export default api;